import React, { useCallback, useMemo } from "react";
import { Image } from "react-bootstrap";
import { useHistory } from "react-router-dom";

import {
	GenericSwitch,
	OverflowTooltip,
	Table,
	TableContainer,
	TableTooltip,
} from "components";
import {
	getApplicableIcon,
	getScheduleAFriendlyId,
	StatusMethodTypes,
} from "mappers";
import {
	ColumnTypes,
	ScheduleATableProps,
	ScheduleAType,
	TableCellProp,
} from "types";

import ScheduleATableActions from "./scheduleATableActions";

import styles from "./scheduleATable.module.scss";

const STATUS_ACTIVE = 0;
const STATUS_INACTIVE = 1;

const ScheduleATable = ({
	data = [],
	sortBy = [],
	count = 0,
	loading = false,
	pageIndex = 0,
	setPaging,
	setSorting,
	tableHeight = "315px",
	userId = "",
	setStatus,
	setDefault,
	isEditable,
}: ScheduleATableProps) => {
	const history = useHistory();

	const handlePageChange = page => {
		setPaging(page);
	};

	const handleSortChange = sort => {
		setSorting(sort);
	};

	const onRowClick = useCallback(
		({ original }) => {
			history.push(`${history.location.pathname}/${original.id}/view`);
		},
		[history],
	);

	const meta = useMemo(
		() => ({
			loading,
			totalItems: count,
		}),
		[loading, count],
	);

	const handleStatusChange = useCallback(
		schedule => {
			if (schedule.status === STATUS_ACTIVE) {
				setStatus({
					userId,
					scheduleId: schedule.id,
					status: STATUS_INACTIVE,
				});
			} else {
				setStatus({
					userId,
					scheduleId: schedule.id,
					status: STATUS_ACTIVE,
				});
			}
		},
		[setStatus, userId],
	);

	const handleDefaultChange = useCallback(
		schedule => {
			setDefault({
				agentId: userId,
				scheduleId: schedule.id,
				isDefault: !schedule.is_default,
			});
		},
		[setDefault, userId],
	);

	const columns: ColumnTypes = useMemo(
		() => [
			{
				Header: "ID",
				accessor: "id",
				classNameHeader: `${styles.cellWithIconHeader}`,
				Cell: ({
					row: { original },
				}: TableCellProp<ScheduleAType & { id: string }>) => {
					return (
						<div
							className="cellWithIcon cut-text-laptop"
							style={{
								paddingLeft: "50px",
							}}
						>
							<TableTooltip
								text={`Buy Rates are ${
									original?.are_fees_not_applicable
										? "not"
										: ""
								} applicable`}
								placement="top"
							>
								<Image
									src={getApplicableIcon(
										!original?.are_fees_not_applicable,
									)}
									className={"iconStatus"}
								/>
							</TableTooltip>
							<OverflowTooltip
								onlyOnLaptop
								title={getScheduleAFriendlyId(original)}
							>
								{getScheduleAFriendlyId(original)}
							</OverflowTooltip>
						</div>
					);
				},
			},
			{
				Header: "Name",
				accessor: "name",
				Cell: ({
					cell,
				}: TableCellProp<ScheduleAType & { id: string }>) => {
					return (
						<OverflowTooltip onlyOnLaptop title={cell?.value}>
							{cell?.value}
						</OverflowTooltip>
					);
				},
			},
			{
				Header: "Processor",
				accessor: "processor",
				Cell: ({
					cell,
				}: TableCellProp<ScheduleAType & { id: string }>) => {
					return (
						<OverflowTooltip onlyOnLaptop title={cell?.value?.name}>
							{cell?.value?.name}
						</OverflowTooltip>
					);
				},
			},
			{
				Header: "Default",
				accessor: "default",
				disableSortBy: true,
				Cell: ({
					row: { original },
				}: TableCellProp<ScheduleAType & { id: string }>) => {
					const isDefault = original?.is_default === true;
					const isAvailable =
						original?.status === StatusMethodTypes.ACTIVE;

					return (
						<div className="d-flex">
							{isEditable ? (
								<GenericSwitch
									name={`isDefault_${original.id}`}
									id={`isDefault_${original.id}`}
									enabled={isDefault}
									onChange={_ =>
										handleDefaultChange(original)
									}
									isDisabled={!isAvailable}
								/>
							) : isDefault ? (
								"Is Default"
							) : (
								""
							)}
						</div>
					);
				},
			},
			{
				Header: "Status",
				accessor: "status",
				Cell: ({
					row: { original },
				}: TableCellProp<ScheduleAType & { id: string }>) => {
					const isAvailable =
						original?.status === StatusMethodTypes.ACTIVE;
					return (
						<div className="d-flex">
							{isEditable && (
								<GenericSwitch
									name={`schedule_${original.id}`}
									id={`schedule_${original.id}`}
									enabled={isAvailable}
									onChange={_ => handleStatusChange(original)}
								/>
							)}
							<OverflowTooltip
								onlyOnLaptop
								title={
									isAvailable ? "Available" : "Unavailable"
								}
								className="ml-3"
							>
								{isAvailable ? "Available" : "Unavailable"}
							</OverflowTooltip>
						</div>
					);
				},
			},
			{
				Header: "Actions",
				accessor: "",
				disableSortBy: true,
				classNameCell: `${styles.actionsCell}`,
				classNameHeader: `${styles.actionsCellHeader}`,
				Cell: ({
					row,
				}: TableCellProp<ScheduleAType & { id: string }>) => {
					return (
						<ScheduleATableActions
							row={row}
							isEditable={isEditable}
						/>
					);
				},
			},
		],
		[handleDefaultChange, handleStatusChange, isEditable],
	);

	return (
		<TableContainer tableHeight={tableHeight}>
			<Table
				columns={columns}
				data={data}
				handleSortChange={handleSortChange}
				handlePageChange={handlePageChange}
				onRowClick={onRowClick}
				meta={meta}
				initialState={{
					sortBy: sortBy,
					pageIndex,
				}}
				stickyFooter
				stickyHeader
				compact
			/>
		</TableContainer>
	);
};

export default ScheduleATable;
